import React, { useState } from "react";
import { ErrorMessage, Field, FieldProps } from "formik";
import { FeatureFlagEnabled, PolyIconButton } from "elements";
import { ResourceFileSelector } from "./";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  inputClassName?: string;
  label?: string;
  name: string;
  type?: string;
  tabIndex?: number;
  placeholder?: string;
  min?: string | number;
  max?: string | number;
  isValid?: boolean;
  validate?: (value: any) => string | undefined | Promise<any>;
  showErrorMessage?: boolean;
  resourceSelector?: boolean;
}

export const MAX_INT = 65535;

export const Input: React.FC<InputProps> = ({
  className,
  inputClassName,
  label,
  name,
  type,
  tabIndex,
  placeholder,
  min,
  max,
  validate,
  showErrorMessage = true,
  onFocus,
  onBlur,
  resourceSelector = false,
  ...rest
}) => {
  const [active, setActive] = useState(false);
  const [resourceSelectorOpen, setResourceSelectorOpen] = useState(false);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setActive(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const createBlurHandler = function(
    fieldOnBlur: (e: React.FocusEvent<any>) => void
  ): (e: React.FocusEvent<any>) => void {
    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const target = event.target;
      setActive(target.value.length > 0);
      if (fieldOnBlur) {
        fieldOnBlur(event);
      }
      if (onBlur) {
        onBlur(event);
      }
    };
    return handleBlur;
  };

  const wrapClasses: Array<string | undefined> = [
    label ? "floating-label-input" : "form-input",
    className
  ];

  const inputClasses: string = [
    "form-control floating-input",
    inputClassName,
    rest.disabled && "readonly"
  ]
    .filter(c => !!c)
    .join(" ");

  const ariaLabel = label ?? name;
  const showRange =
    (min || min === 0) &&
    (max || max === 0) &&
    Number.parseInt(`${max}`) < MAX_INT;

  const fieldType = type ?? "text";

  if ("password" === fieldType && !rest.autoComplete) {
    rest.autoComplete = "off";
  }

  if ("checkbox" === fieldType) {
    wrapClasses.push("checkbox");
  }

  return (
    <Field name={name} type={fieldType} validate={validate}>
      {({ field, meta, form }: FieldProps) => {
        const { onBlur: onFieldBlur, ...restField } = field;
        const handleBlur = createBlurHandler(onFieldBlur);

        if (meta.touched && meta.error) {
          wrapClasses.push("has-error");
        }
        // Show active when there is a displayable value
        // Check for value for catching when input is controlled by parent.
        if (
          active ||
          field.value ||
          !(typeof field.value === "string") ||
          rest.value
        ) {
          wrapClasses.push("active");
        }
        if (showRange) {
          wrapClasses.push("range");
        }
        if (resourceSelector) {
          wrapClasses.push("resource-selector");
        }
        return (
          <div
            className={wrapClasses.filter(c => !!c).join(" ")}
            data-testid={`input-container-for-${name}`}
          >
            <div className="label-container">
              {label && (
                <label
                  className="floating-label"
                  htmlFor={field.name + "-input"}
                >
                  {label}
                </label>
              )}
              <input
                type={fieldType}
                id={field.name + "-input"}
                role="textbox"
                aria-label={ariaLabel}
                className={inputClasses}
                onFocus={handleFocus}
                onBlur={handleBlur}
                tabIndex={tabIndex || -1}
                placeholder={placeholder}
                data-testid="value-input"
                {...restField}
                {...rest}
              />
              {showRange && (
                <span className="limiter after">{`(${min} - ${max})`}</span>
              )}
              <FeatureFlagEnabled
                feature="RESOURCE_FILES"
                andIf={resourceSelector}
              >
                <PolyIconButton
                  type="button"
                  icon="resource_file"
                  className="ml-1"
                  onClick={() => setResourceSelectorOpen(true)}
                  disabled={rest.disabled}
                  data-testid="select-resource-file"
                />
                {resourceSelectorOpen && (
                  <ResourceFileSelector
                    selectedFile={field.value}
                    onSelect={fileName => {
                      form.setFieldValue(name, fileName);
                    }}
                    onClose={() => setResourceSelectorOpen(false)}
                  />
                )}
              </FeatureFlagEnabled>
            </div>
            {showErrorMessage && (
              <ErrorMessage
                component="div"
                className="error-message"
                data-testid={`error-msg-for-${name}`}
                name={name}
              />
            )}
          </div>
        );
      }}
    </Field>
  );
};
